'use client';

import { useSidebar } from '@/contexts/SidebarContext';
import { GuideProvider } from '@/features/guided-help';
import { ManagerGuard } from '@/routes/ManagerGuard';
import type React from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';

const Layout: React.FC = () => {
  const { isOpen, isCollapsed, toggleMobile } = useSidebar();

  // GuideProvider wraps the guard, not the other way around: ManagerGuard
  // renders a full-screen Profile of its own when the manager has no CUIT, and
  // that Profile calls useAutoStartTour. Inside the guard it would fall outside
  // the provider and throw.
  return (
    <GuideProvider>
      <ManagerGuard>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <Sidebar />
          {/* h-dvh rather than inset-0: a fixed element resolves against the
              large viewport, so on mobile the bottom of the drawer (the logout
              button) would sit behind the browser toolbar. */}
          {isOpen && (
            <div
              className="fixed inset-x-0 top-0 z-50 h-dvh bg-black bg-opacity-50 lg:hidden"
              onClick={toggleMobile}
            >
              <div
                className="absolute left-0 top-0 h-full w-56 border-r border-gray-200 bg-gray-50 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <Sidebar mobile />
              </div>
            </div>
          )}
          <div
            className={`min-h-screen bg-gray-100 pt-16 transition-all duration-300 ${
              isCollapsed ? 'lg:ml-16' : 'lg:ml-56'
            }`}
          >
            {/* The support banner is fixed to the bottom of the viewport and
                would otherwise cover the last strip of a long page. Outside a
                support session `--support-banner-h` is 0px, which leaves the
                p-6 gutter exactly as it was. */}
            <main className="bg-gray-100 p-6 pb-[calc(1.5rem_+_var(--support-banner-h))]">
              <Outlet />
            </main>
          </div>
        </div>
      </ManagerGuard>
    </GuideProvider>
  );
};

export default Layout;
